import { useState } from 'react';
import { GripVertical, Trash2, Image, Plus, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Lightbox } from './Lightbox';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function ImageManager({ propertyId, images = [], onUpdate }) {
  const [isOpen, setIsOpen] = useState(false);
  const [localImages, setLocalImages] = useState(images);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openManager = () => {
    setLocalImages([...images]);
    setIsOpen(true);
  };

  const getImageUrl = (path) => `${API}/files/${path}`;

  const handleUpload = async (files) => {
    setUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append('file', files[i]);
        const { data } = await axios.post(`${API}/properties/${propertyId}/images`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setLocalImages((prev) => [...prev, data.path]);
      }
      toast.success(`${files.length} immagine/i caricata/e`);
      onUpdate();
    } catch {
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async (imagePath) => {
    try {
      await axios.delete(`${API}/properties/${propertyId}/images?image_path=${encodeURIComponent(imagePath)}`, {
        withCredentials: true,
      });
      setLocalImages((prev) => prev.filter((p) => p !== imagePath));
      toast.success('Immagine rimossa');
      onUpdate();
    } catch {
      toast.error('Errore nella rimozione');
    }
  };

  const handleDragStart = (index) => {
    setDragIndex(index);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    const newImages = [...localImages];
    const dragged = newImages.splice(dragIndex, 1)[0];
    newImages.splice(index, 0, dragged);
    setLocalImages(newImages);
    setDragIndex(index);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
  };

  const saveOrder = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/properties/${propertyId}/images/reorder`, {
        images: localImages,
      }, { withCredentials: true });
      toast.success('Ordine salvato');
      onUpdate();
    } catch {
      toast.error('Errore nel salvataggio ordine');
    } finally {
      setSaving(false);
    }
  };

  const hasOrderChanged = JSON.stringify(localImages) !== JSON.stringify(images);

  return (
    <>
      <Button variant="ghost" size="sm" onClick={openManager} title="Gestisci foto" data-testid={`manage-images-${propertyId}`}>
        <Image size={16} className="text-primary" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Gestione Foto</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Upload area */}
            <label className="flex items-center justify-center border-2 border-dashed border-border rounded-xl p-6 cursor-pointer hover:bg-background-alt transition-colors" data-testid="image-upload-area">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => e.target.files && handleUpload(e.target.files)}
                disabled={uploading}
              />
              <div className="text-center">
                <Plus size={24} className="mx-auto text-text-muted mb-2" />
                <p className="font-sans text-sm text-text-muted">
                  {uploading ? 'Caricamento...' : 'Clicca per aggiungere foto'}
                </p>
                <p className="font-sans text-xs text-text-muted mt-1">Puoi selezionare più foto</p>
              </div>
            </label>

            {/* Images grid - draggable */}
            {localImages.length > 0 ? (
              <>
                <div className="flex items-center justify-between">
                  <p className="font-sans text-sm text-text-muted">{localImages.length} foto - Trascina per riordinare</p>
                  {hasOrderChanged && (
                    <Button size="sm" className="bg-primary hover:bg-primary-hover" onClick={saveOrder} disabled={saving} data-testid="save-order-btn">
                      {saving ? 'Salvataggio...' : 'Salva Ordine'}
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {localImages.map((img, index) => (
                    <div
                      key={img}
                      draggable
                      onDragStart={() => handleDragStart(index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`relative group rounded-lg overflow-hidden border-2 transition-all cursor-grab active:cursor-grabbing ${
                        dragIndex === index ? 'border-primary opacity-60 scale-95' : 'border-transparent hover:border-primary/30'
                      }`}
                      data-testid={`image-item-${index}`}
                    >
                      <div className="aspect-[4/3] relative">
                        <img src={getImageUrl(img)} alt={`Foto ${index + 1}`} className="w-full h-full object-cover" />
                        {/* Overlay */}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                          <button
                            onClick={() => setLightboxIndex(index)}
                            className="p-2 bg-white/90 rounded-full hover:bg-white transition-colors"
                            title="Visualizza"
                          >
                            <Eye size={16} />
                          </button>
                          <button
                            onClick={() => handleRemove(img)}
                            className="p-2 bg-red-500/90 rounded-full hover:bg-red-500 text-white transition-colors"
                            title="Elimina"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        {/* Position badge */}
                        <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-sans px-2 py-1 rounded flex items-center gap-1">
                          <GripVertical size={12} />
                          {index + 1}
                        </div>
                        {index === 0 && (
                          <div className="absolute bottom-2 left-2 bg-primary text-white text-xs font-sans px-2 py-1 rounded">
                            Copertina
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-center text-text-muted font-sans py-8">Nessuna foto caricata</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={localImages.map(getImageUrl)}
          initialIndex={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </>
  );
}
